import React, { useCallback, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FormikContext } from "formik";

import {
  ButtonPrimaryTransparent,
  ButtonSecondary,
  DoubleContent,
  Headline,
  InputControl,
  List,
  Loading,
  Modal,
  ReCaptchaControl,
  SectionContent,
  Text,
  ButtonSecondaryInverse,
} from "../../../../components";
import { links } from "../../../../config";
import { ValoresAReceberForm } from "../../../../forms";
import * as S from "./ValoresAReceber.Style";
import MoedasImg from "../../../../assets/images/consorcio/ilustracao-moedas.svg";
import { useValoresReceberConsorcios } from "../../../../hooks";
import { ModalContext } from "../../../../contexts";

const ModalResultadoBody = ({ existeValoresAReceber }) => {
  const { closeHandle } = useContext(ModalContext);

  const textoComAspas = '"Fazer uma solicitação"';
  return (
    <S.ModalResultadoBody>
      <Headline text="Valores a receber em grupos encerrados" nivel="5" />
      {!existeValoresAReceber ? (
        <Text>Você não possui valores a receber.</Text>
      ) : (
        <>
          <Text>
            Você possui valores a receber. Para solicitar a devolução, siga os
            passos abaixo:
          </Text>
          <List styleType="disc">
            <li>
              <Text>Acesse a área do cliente;</Text>
            </li>
            <li>
              <Text>
                Entre na sua cota e acesse o menu: Movimentações na cota;
              </Text>
            </li>
            <li>
              <Text> Em Registrar solicitação, acesse: {textoComAspas};</Text>
            </li>
            <li>
              <Text>
                {" "}
                No assunto principal, selecione: Financeiro - Solicitar
                pagamento;
              </Text>
            </li>
            <li>
              <Text>
                Em seguida, selecione a opção: Valor não procurado após
                encerramento;
              </Text>
            </li>
            <li>
              <Text>Informe os dados bancários e aguarde o pagamento.</Text>
            </li>
          </List>
          <ButtonPrimaryTransparent
            as={ButtonSecondaryInverse}
            target="_blank"
            label="Área do cliente - Pessoa Física"
            href={links.consorcio.areaLogada}
          />
          <S.ModalCloseButton
            as={ButtonPrimaryTransparent}
            onClick={closeHandle}
            label="Fechar"
          />
        </>
      )}
    </S.ModalResultadoBody>
  );
};

ModalResultadoBody.propTypes = {
  existeValoresAReceber: PropTypes.bool.isRequired,
};

const FormValoresAReceber = ({ resultadoConsulta }) => {
  const {
    dirty,
    isValid,
    setFieldValue,
    submitForm,
    resetForm,
    values: { resultado },
  } = useContext(FormikContext);

  const closeResultadoHandle = useCallback(
    (evt) => {
      resetForm(evt);
    },
    [resetForm]
  );

  const existeValoresAReceber = useMemo(
    () => resultadoConsulta?.existeValoresAReceber,
    [resultadoConsulta]
  );

  useEffect(() => {
    setFieldValue("resultado", resultadoConsulta);
    return () => {
      setFieldValue("resultado", null);
    };
  }, [resultadoConsulta, setFieldValue]);

  return (
    <>
      <InputControl
        label="Informe o CPF ou CNPJ"
        placeholder="Apenas números"
        name="cpfCnpj"
        mask="cpf/cnpj"
      />
      <ReCaptchaControl />
      <S.ConsultarValoresButton
        as={ButtonSecondary}
        label="Consultar"
        disabled={!dirty || !isValid}
        onClick={submitForm}
      />
      <Modal
        show={resultado !== null}
        onClose={closeResultadoHandle}
        headerModal={null}
        opacityGlass={1}
      >
        <ModalResultadoBody existeValoresAReceber={existeValoresAReceber} />
      </Modal>
    </>
  );
};

FormValoresAReceber.defaultProps = {
  resultadoConsulta: null,
};

FormValoresAReceber.propTypes = {
  resultadoConsulta: PropTypes.shape({
    existeValoresAReceber: PropTypes.bool,
  }),
};

const ValoresAReceber = () => {
  const {
    loading,
    resultadoConsulta,
    verificarValoresAReceber,
  } = useValoresReceberConsorcios();

  const onSubmitHandle = useCallback(
    (dadosEntrada) => {
      verificarValoresAReceber({ ...dadosEntrada });
    },
    [verificarValoresAReceber]
  );

  return (
    <Loading loading={loading}>
      <SectionContent>
        <DoubleContent
          leftContent={
            <>
              <Headline nivel="2" styleAs="5">
                Valores a receber em grupos encerrados
              </Headline>
              <Text>
                Após o encerramento do grupo, se houver dinheiro sobrando, um
                rateio é feito com todos os consorciados e a divisão será
                proporcional ao valor do seu crédito. Preencha os campos e
                verifique se você tem algum valor a receber
              </Text>
              <S.ValoresAReceberImg src={MoedasImg} />
            </>
          }
          rightContent={
            <ValoresAReceberForm
              onSubmit={onSubmitHandle}
              form={FormValoresAReceber}
              resultadoConsulta={resultadoConsulta}
            />
          }
        />
      </SectionContent>
    </Loading>
  );
};

export default ValoresAReceber;
